/**
 * LLM-Assisted Refinement Panel
 *
 * Allows admins to request episode modifications via LLM:
 * - Text input for refinement instructions (max 500 chars)
 * - Apply Changes button triggers LLM refinement
 * - Loading state during refinement (10-30 seconds)
 * - Displays changes made after successful refinement
 *
 * Story: 11.5 - Basic Episode Generator
 * Acceptance Criteria: AC 4 - Task 14
 */

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { type GeneratedEpisode, useRefineEpisode } from '../services/generationApi';

// ============================================================================
// Types
// ============================================================================

interface RefinementPanelProps {
  episode: GeneratedEpisode;
  onEpisodeRefined: (episode: GeneratedEpisode, changesMade: string[]) => void;
  initialInstructions?: string;
}

// ============================================================================
// Component
// ============================================================================

export function RefinementPanel({
  episode,
  onEpisodeRefined,
  initialInstructions,
}: RefinementPanelProps) {
  const [instructions, setInstructions] = useState('');
  const [changesMade, setChangesMade] = useState<string[]>([]);
  const [showChanges, setShowChanges] = useState(false);

  const refineEpisodeMutation = useRefineEpisode();

  const isLoading = refineEpisodeMutation.isPending;
  const characterCount = instructions.length;
  const maxChars = 500;

  // Pre-fill instructions from judge recommendations
  useEffect(() => {
    if (initialInstructions) {
      setInstructions(initialInstructions);
    }
  }, [initialInstructions]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleApplyChanges = () => {
    if (!instructions.trim()) {
      toast.error('Please enter refinement instructions');
      return;
    }

    if (instructions.length > maxChars) {
      toast.error(`Instructions must be ${maxChars} characters or less`);
      return;
    }

    refineEpisodeMutation.mutate(
      { episode, instructions: instructions.trim() },
      {
        onSuccess: (response) => {
          setChangesMade(response.changesMade);
          setShowChanges(true);
          setInstructions('');
          onEpisodeRefined(response.episode, response.changesMade);
          toast.success('Episode refined successfully!');
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: { data?: { error?: { message?: string } } };
            message?: string;
          };
          const errorMessage =
            err.response?.data?.error?.message || err.message || 'Failed to refine episode';
          toast.error(errorMessage);
        },
      }
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="bg-white rounded-lg shadow-md p-6" data-testid="refinement-panel">
      <h2 className="text-xl font-bold mb-4">LLM-Assisted Refinement</h2>

      <p className="text-sm text-gray-600 mb-4">
        Describe the changes you want to make, and the LLM will update the episode accordingly.
      </p>

      {/* Instructions Input */}
      <div className="mb-4">
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={4}
          placeholder="e.g., 'Make personas more distinct', 'Add more challenging goals', 'Simplify the context'"
          disabled={isLoading}
          maxLength={maxChars}
          data-testid="instructions-input"
        />

        <div className="flex justify-between items-center mt-1">
          <p className="text-xs text-gray-500">Describe what you want to change</p>
          <p
            className={`text-sm ${characterCount > maxChars ? 'text-red-500' : 'text-gray-500'}`}
            data-testid="character-count"
          >
            {characterCount} / {maxChars}
          </p>
        </div>
      </div>

      {/* Apply Changes Button */}
      <div className="mb-4">
        <button
          type="button"
          onClick={handleApplyChanges}
          disabled={isLoading || !instructions.trim() || instructions.length > maxChars}
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          data-testid="apply-changes-button"
        >
          {isLoading ? 'Applying changes...' : 'Apply Changes with LLM'}
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div
          className="flex items-center justify-center space-x-3 p-4 bg-blue-50 rounded-md"
          data-testid="loading-state"
        >
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span className="text-sm text-gray-700">Applying changes... (10-30 seconds)</span>
        </div>
      )}

      {/* Changes Made Display (Collapsible) */}
      {changesMade.length > 0 && (
        <div className="border border-gray-300 rounded-md p-4" data-testid="changes-made-section">
          <button
            type="button"
            onClick={() => setShowChanges(!showChanges)}
            className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 w-full"
            data-testid="changes-toggle"
          >
            <span className="mr-2">{showChanges ? '▼' : '►'}</span>
            Changes Made ({changesMade.length})
          </button>

          {showChanges && (
            <ul className="list-disc list-inside mt-3 space-y-1" data-testid="changes-list">
              {changesMade.map((change, index) => (
                <li key={index} className="text-sm text-gray-600" data-testid={`change-${index}`}>
                  {change}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
